import {createExerciseModal, findInitialExercises} from "./exerciseModal";
import {createHowToModal} from "./howToModal";

const settingsCookieName = "strengthlevelsettings";
const unitDefinitions = {
    kg: {step: 0.1, remainderSymbol: null, remainderMultiple: null},
    lb: {step: 0.1, remainderSymbol: null, remainderMultiple: null},
    st: {step: 0.1, remainderSymbol: "lb", remainderMultiple: 14},
};

function getTimezoneOffset() {
    return Math.round(-new Date().getTimezoneOffset() / 60);
}

function readCookie(name) {
    const prefix = name + "=";
    const parts = document.cookie ? document.cookie.split(";") : [];
    for (let i = 0; i < parts.length; i++) {
        const part = parts[i].trim();
        if (part.indexOf(prefix) === 0) {
            try {
                return decodeURIComponent(part.substring(prefix.length));
            } catch (e) {
                return null;
            }
        }
    }
    return null;
}

function writeCookie(name, value, days) {
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = name + "=" + encodeURIComponent(value) + "; expires=" + expires.toUTCString() + "; path=/; Secure; SameSite=Strict";
}

function buildQuery(params) {
    return Object.keys(params)
        .filter(function (key) {
            return params[key] !== undefined && params[key] !== null && params[key] !== "";
        })
        .map(function (key) {
            return encodeURIComponent(key) + "=" + encodeURIComponent(params[key]);
        })
        .join("&");
}

function getApiUrl(path) {
    const baseUrl = window.apiUrl || "/api/";
    return baseUrl.replace(/\/?$/, "/") + path.replace(/^\//, "");
}

function getJson(path, params) {
    const query = buildQuery(params);
    const url = getApiUrl(path) + (query ? "?" + query : "");
    return fetch(url, {
        headers: {
            "Accept": "application/json",
        },
        credentials: "same-origin",
    }).then(function (response) {
        if (!response.ok) {
            throw new Error("Could not load exercise");
        }
        return response.json();
    });
}

function findExercise(exercises, exerciseSlug) {
    const list = exercises && exercises.data ? exercises.data : [];
    for (let i = 0; i < list.length; i++) {
        if (list[i].name_url === exerciseSlug) {
            return list[i];
        }
    }
    return null;
}

function setDisabled(container, disabled) {
    if (!container) {
        return;
    }
    const elements = container.querySelectorAll("input, select, button, textarea");
    for (let i = 0; i < elements.length; i++) {
        elements[i].disabled = disabled;
    }
}

function setFieldVisible(field, visible) {
    if (!field) {
        return;
    }
    field.style.display = visible ? "" : "none";
    setDisabled(field, !visible);
}

function setAllValues(elements, value) {
    for (let i = 0; i < elements.length; i++) {
        elements[i].value = value;
    }
}

function getEnabledValue(elements, fallback) {
    for (let i = 0; i < elements.length; i++) {
        if (!elements[i].disabled && elements[i].value) {
            return elements[i].value;
        }
    }
    return fallback;
}

function track(eventName, eventCategory, eventLabel) {
    if (typeof window.gtag === "function") {
        window.gtag("event", eventName, {
            "event_category": eventCategory,
            "event_label": eventLabel,
        });
    }
}

export function initPercentileForm(config) {
    config = config || {};

    const form = document.getElementById(config.formId || "formCalc");
    if (!form) {
        return;
    }

    const defaultExercise = config.defaultExercise || {};
    const defaultExerciseSlug = defaultExercise.name_url || "";
    const initialExercises = findInitialExercises();
    const defaultLiftViewModel = {
        gender: "male",
        ageyears: "",
        liftmass: "",
        liftmassunit: "lb",
        bodymass: "",
        bodymass_minor: "",
        bodymassunit: "lb",
        repetitions: 1,
        time: "",
        exercise: defaultExerciseSlug,
        assistancemass: "",
        extramass: "",
        variation: "bodyweight",
    };

    let liftViewModel = Object.assign({}, defaultLiftViewModel, config.serverLiftViewModel || {});
    liftViewModel.variation = liftViewModel.variation || "bodyweight";

    const cookieContent = readCookie(settingsCookieName);
    if (cookieContent) {
        try {
            const storedLiftViewModel = JSON.parse(cookieContent);
            if (storedLiftViewModel) {
                liftViewModel = Object.assign(liftViewModel, storedLiftViewModel);
                liftViewModel.liftmassunit = (liftViewModel.liftmassunit || "").trim();
                liftViewModel.bodymassunit = (liftViewModel.bodymassunit || "").trim();
            }
        } catch (e) {
        }
    }

    liftViewModel.exercise = liftViewModel.exercise || defaultExerciseSlug;

    const bodyMassUnitSelect = form.querySelector("[name='bodymassunit']");
    const bodyMassInput = form.querySelector("[name='bodymass']");
    const bodyMassMinorInput = form.querySelector("[name='bodymass_minor']");
    const bodyMassMinorContainer = document.getElementById("bodymassminorcontainer");
    const bodyMassMinorUnitContainer = document.getElementById("bodymassminorunitcontainer");
    const bodyMassMinorUnit = document.getElementById("bodymassminorunit");
    const liftMassUnitSelects = form.querySelectorAll("[name='liftmassunit']");
    const exerciseSelect = document.getElementById("exercise");
    const exerciseSelectContainer = document.getElementById("exerciseselectcontainer");
    const exerciseHiddenInput = document.getElementById("exercisehidden");
    const exerciseReadonlyInput = document.getElementById("exercisereadonly");
    const exerciseLabel = form.querySelector("label[for='exercise']");
    const standardsLink = document.getElementById("standardsLink");
    const howToLink = document.getElementById("howToLink");
    const variationField = document.getElementById("variationfield");
    const variationSelect = document.getElementById("variation");
    const assistanceMassField = document.getElementById("assistancemassfield");
    const extraMassField = document.getElementById("extramassfield");
    const liftMassField = document.getElementById("liftmassfield");
    const repetitionsField = document.getElementById("repetitionsfield");
    const timeField = document.getElementById("timefield");
    const liftMassLabel = document.getElementById("liftmasslabel");
    const liftMassHelp = document.getElementById("liftmasshelp");
    const timezoneInput = form.querySelector("[name='timezone']");
    let currentExercise = defaultExercise;

    function setFormValue(name, value) {
        const elements = form.querySelectorAll("[name='" + name + "']");
        setAllValues(elements, value == null ? "" : value);
    }

    function setLiftMassUnit(value) {
        const unit = value === "kg" ? "kg" : "lb";
        setAllValues(liftMassUnitSelects, unit);
    }

    function getLiftMassUnit() {
        return getEnabledValue(liftMassUnitSelects, liftViewModel.liftmassunit || "lb");
    }

    function syncExerciseInputs(exerciseSlug, exerciseName) {
        exerciseSlug = exerciseSlug || defaultExerciseSlug;
        if (exerciseHiddenInput) {
            exerciseHiddenInput.value = exerciseSlug;
        }
        if (exerciseReadonlyInput) {
            exerciseReadonlyInput.value = exerciseName || exerciseSlug;
        }
    }

    function updateViewModelFromForm() {
        const formData = new FormData(form);
        formData.forEach(function (value, key) {
            liftViewModel[key] = value;
        });
        liftViewModel.exercise = exerciseHiddenInput && exerciseHiddenInput.value ||
            exerciseSelect && exerciseSelect.value ||
            liftViewModel.exercise ||
            defaultExerciseSlug;
        syncExerciseInputs(liftViewModel.exercise, currentExercise ? currentExercise.name : "");
        delete liftViewModel.timezone;
        delete liftViewModel.source;
    }

    function updateBodyMassMinor() {
        if (!bodyMassUnitSelect) {
            return;
        }
        const unit = unitDefinitions[bodyMassUnitSelect.value] || unitDefinitions.lb;
        const hasRemainder = !!unit.remainderSymbol;

        if (bodyMassInput) {
            bodyMassInput.step = unit.step;
        }
        if (bodyMassMinorInput) {
            bodyMassMinorInput.disabled = !hasRemainder;
            bodyMassMinorInput.required = hasRemainder;
            bodyMassMinorInput.step = unit.step;
            bodyMassMinorInput.max = hasRemainder ? unit.remainderMultiple - unit.step : "";
        }
        if (bodyMassMinorContainer) {
            bodyMassMinorContainer.classList.toggle("is-hidden", !hasRemainder);
        }
        if (bodyMassMinorUnitContainer) {
            bodyMassMinorUnitContainer.classList.toggle("is-hidden", !hasRemainder);
        }
        if (bodyMassMinorUnit && hasRemainder) {
            bodyMassMinorUnit.textContent = bodyMassMinorUnit.dataset[unit.remainderSymbol + "Label"] || unit.remainderSymbol;
        }
    }

    function updateLiftMassSteps() {
        const unit = unitDefinitions[getLiftMassUnit()] || unitDefinitions.lb;
        const inputs = [
            document.getElementById("liftmass"),
            document.getElementById("assistancemass"),
            document.getElementById("extramass"),
        ];
        for (let i = 0; i < inputs.length; i++) {
            if (inputs[i]) {
                inputs[i].step = unit.step;
            }
        }
    }

    function updateLinks(exercise) {
        if (standardsLink && exercise.name_url) {
            standardsLink.href = standardsLink.dataset.hrefTemplate.replace("{exercise-slug}", exercise.name_url);
            standardsLink.title = (exercise.name || "") + " " + (standardsLink.dataset.titleSuffix || "Strength Standards");
        }
        if (howToLink && exercise.name_url) {
            howToLink.href = howToLink.dataset.hrefTemplate.replace("{exercise-slug}", exercise.name_url);
        }
    }

    function updateExerciseFields() {
        const category = currentExercise ? currentExercise.category : "";
        const isBodyweightTime = currentExercise && currentExercise.type === "Bodyweight/Time";
        const isWeightDistance = currentExercise && currentExercise.type === "Weight/Distance";
        const isBodyweight = category === "Bodyweight" && !isBodyweightTime;
        const variation = variationSelect ? variationSelect.value : "bodyweight";

        setFieldVisible(variationField, isBodyweight);
        setFieldVisible(liftMassField, !isBodyweight && !isBodyweightTime);
        setFieldVisible(assistanceMassField, isBodyweight && variation === "assisted");
        setFieldVisible(extraMassField, isBodyweight && variation === "weighted");
        setFieldVisible(repetitionsField, !isBodyweightTime && !isWeightDistance);
        setFieldVisible(timeField, isBodyweightTime);

        if (liftMassLabel) {
            if (category === "Dumbbell") {
                liftMassLabel.textContent = liftMassLabel.dataset.dumbbellLabel;
            } else if (category === "Cable") {
                liftMassLabel.textContent = liftMassLabel.dataset.cableLabel;
            } else {
                liftMassLabel.textContent = liftMassLabel.dataset.defaultLabel;
            }
        }

        if (liftMassHelp) {
            let helpText = "";
            if (category === "Dumbbell") {
                helpText = liftMassHelp.dataset.dumbbellHelp;
            } else if (category === "Cable") {
                helpText = liftMassHelp.dataset.cableHelp;
            }
            liftMassHelp.textContent = helpText;
            liftMassHelp.classList.toggle("is-hidden", !helpText);
        }

        updateLiftMassSteps();
    }

    function selectExercise(exercise) {
        currentExercise = exercise && exercise.name_url ? exercise : defaultExercise;
        liftViewModel.exercise = currentExercise.name_url || defaultExerciseSlug;
        syncExerciseInputs(liftViewModel.exercise, currentExercise.name || "");

        updateLinks(currentExercise);
        updateExerciseFields();
    }

    function deferExerciseControlEnhancement() {
        const finishNativeSelect = function () {
            if (exerciseSelect.disabled) {
                return;
            }
            enhanceExerciseControl();
            resolveExercise(liftViewModel.exercise || defaultExerciseSlug);
        };

        const onSelectChange = function () {
            liftViewModel.exercise = exerciseSelect.value || liftViewModel.exercise || defaultExerciseSlug;
            syncExerciseInputs(liftViewModel.exercise, "");
            finishNativeSelect();
        };

        exerciseSelect.addEventListener("change", onSelectChange);
        exerciseSelect.addEventListener("blur", finishNativeSelect);
    }

    function enhanceExerciseControl() {
        if (!exerciseSelect || !exerciseHiddenInput || !exerciseReadonlyInput) {
            return;
        }
        if (document.activeElement === exerciseSelect) {
            deferExerciseControlEnhancement();
            return false;
        }

        syncExerciseInputs(liftViewModel.exercise, currentExercise ? currentExercise.name : "");
        exerciseSelect.disabled = true;
        if (exerciseSelectContainer) {
            exerciseSelectContainer.classList.add("is-hidden");
        }
        exerciseHiddenInput.disabled = false;
        exerciseReadonlyInput.disabled = false;
        exerciseReadonlyInput.classList.remove("is-hidden");

        const openModal = function (event) {
            event.preventDefault();
            if (exerciseModal) {
                exerciseModal.showModal();
            }
        };
        exerciseReadonlyInput.addEventListener("click", openModal);
        if (exerciseLabel) {
            exerciseLabel.addEventListener("click", openModal);
        }
    }

    function resolveExercise(exerciseSlug) {
        const requestedExercise = exerciseSlug || defaultExerciseSlug;
        if (!requestedExercise) {
            return;
        }

        const cachedExercise = findExercise(initialExercises, requestedExercise);
        if (cachedExercise) {
            selectExercise(cachedExercise);
            return;
        }

        getJson("exercises", {
            name_url: requestedExercise,
            limit: 1,
            "exercise.fields": "category,type,name_url,bodypart,count,aliases,icon_url",
            standard: "true",
        }).then(function (response) {
            if (liftViewModel.exercise === requestedExercise) {
                selectExercise(response.data && response.data.length > 0 ? response.data[0] : defaultExercise);
            }
        }).catch(function () {
            if (liftViewModel.exercise === requestedExercise) {
                selectExercise(defaultExercise);
            }
        });
    }

    function applyViewModel() {
        Object.keys(liftViewModel).forEach(function (key) {
            if (key === "liftmassunit") {
                setLiftMassUnit(liftViewModel[key]);
            } else if (key === "exercise" && document.activeElement === exerciseSelect) {
                return;
            } else {
                setFormValue(key, liftViewModel[key]);
            }
        });
        updateBodyMassMinor();
        updateLiftMassSteps();
    }

    if (timezoneInput) {
        timezoneInput.value = getTimezoneOffset();
    }

    applyViewModel();

    const exerciseModal = createExerciseModal({
        root: "#exerciseModal",
        initialExercises: initialExercises,
        onSelect: selectExercise,
    });
    const howToModal = createHowToModal({
        root: "#howToModal",
    });

    if (enhanceExerciseControl() !== false) {
        resolveExercise(liftViewModel.exercise || defaultExerciseSlug);
    }

    if (bodyMassUnitSelect) {
        bodyMassUnitSelect.addEventListener("change", function () {
            if (bodyMassUnitSelect.value !== "st") {
                setLiftMassUnit(bodyMassUnitSelect.value);
            }
            updateBodyMassMinor();
            updateLiftMassSteps();
        });
    }

    for (let i = 0; i < liftMassUnitSelects.length; i++) {
        liftMassUnitSelects[i].addEventListener("change", function (event) {
            setLiftMassUnit(event.target.value);
            if (bodyMassUnitSelect && bodyMassUnitSelect.value !== "st") {
                bodyMassUnitSelect.value = event.target.value;
                updateBodyMassMinor();
            }
            updateLiftMassSteps();
        });
    }

    if (variationSelect) {
        variationSelect.addEventListener("change", function () {
            liftViewModel.variation = variationSelect.value;
            updateExerciseFields();
        });
    }

    if (standardsLink) {
        standardsLink.addEventListener("click", function () {
            track("calculator-action", "standards-page", "show");
        });
    }

    if (howToLink) {
        howToLink.addEventListener("click", function (event) {
            if (howToModal && currentExercise && currentExercise.id) {
                event.preventDefault();
                howToModal.showModal(currentExercise.id);
                track("calculator-action", "howto-modal", "show");
                return;
            }
            track("calculator-action", "howto-page", "show");
        });
    }

    form.addEventListener("submit", function () {
        updateViewModelFromForm();
        writeCookie(settingsCookieName, JSON.stringify(liftViewModel), 365 * 5);
    });

}
